import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { ApiError, get } from '../api.js';
import { t } from '../i18n.js';
import { Link } from '../router.js';
import { useSession } from '../session.js';
import { fmtDateTime, pad2 } from '../util.js';
import { realtime, type WsMessage } from '../ws.js';
import { FeedItemDialog, FeedThumb, type FeedItemDto, type FeedSourceDto } from './feeds.js';
import { Dialog } from './rundown-dialogs.js';
import { featuredLayout, type FeaturedLayout } from '../home-layout.js';

/**
 * The home page: the newsroom's start screen. The clock and the greeting, the quick actions,
 * today's bulletins and the latest stories of the department, and the newest items of the
 * incoming feeds, split into the local wires and the rest. Everything refreshes by itself: once a
 * minute, and at once when the server announces new feed items. Each user may choose which feeds
 * their own start screen shows.
 */

interface TodayRundown {
  id: string;
  programmeId: string;
  name: string;
  status: string;
  revision: number;
  count: number;
}
interface DayStory {
  id: string;
  title: string;
  displayNumber: string | null;
  createdBy: string | null;
  programmeName: string;
  rundownId: string | null;
  totalS: number;
  updatedAt: string;
}

const LOCAL_GROUP = /κύπρ|cyprus/i;
const NO_ITEMS: FeedItemDto[] = [];
const NO_SOURCES: FeedSourceDto[] = [];
/** Picture cards across the top: smaller cards, more of them, before the two columns. */
/** The most pictures the strip may take; it shows two full rows of them, as many across as fit. */
const FEATURED_MAX = 24;
const PER_COLUMN = 8;

/** The strip's layout at its current width (two columns on a phone, bigger cards on a wall); 0 columns until measured. */
function useStripLayout(el: HTMLDivElement | null, ready: boolean): FeaturedLayout {
  const [layout, setLayout] = useState<FeaturedLayout>({ cols: 0, cardW: 176, scale: 1 });
  useLayoutEffect(() => {
    if (!el || !ready) return;
    const measure = () => {
      const w = el.clientWidth;
      if (w <= 0) return;
      setLayout(featuredLayout(w));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    window.addEventListener('resize', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [el, ready]);
  return layout;
}

const ago = (iso: string) => {
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60_000));
  if (m < 1) return t('justNow');
  if (m < 60) return `${m}′`;
  if (m < 48 * 60) return `${Math.floor(m / 60)}h ${m % 60}′`;
  return fmtDateTime(iso).slice(0, 10);
};

const greeting = (h: number) => (h < 12 ? t('homeGoodMorning') : h < 18 ? t('homeGoodAfternoon') : t('homeGoodEvening'));

function useClock(): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const h = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(h);
  }, []);
  return now;
}

/** Load once, then again every minute and whenever `bump` changes; a 403 hides the section instead of showing an error. */
function usePolled<T>(url: string | null, everyMs = 60_000, bump = 0): { data: T | null; error: string | null; at: Date | null } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [at, setAt] = useState<Date | null>(null);
  useEffect(() => {
    if (!url) return;
    let cancelled = false;
    const run = () =>
      get<T>(url)
        .then((r) => {
          if (cancelled) return;
          setData(r);
          setAt(new Date());
          setError(null);
        })
        .catch((e) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.status === 403) return;
          setError(e instanceof ApiError ? e.message : String(e));
        });
    run();
    const h = window.setInterval(run, everyMs);
    return () => {
      cancelled = true;
      window.clearInterval(h);
    };
  }, [url, everyMs, bump]);
  return { data, error, at };
}

function FeedRow({ it, canCreate, onOpen }: { it: FeedItemDto; canCreate: boolean; onOpen: (it: FeedItemDto) => void }) {
  return (
    <li className="home-feed-row">
      {it.imageUrl && <FeedThumb className="home-feed-thumb" src={it.imageUrl} />}
      <div className="home-feed-text">
        <div className="home-feed-meta">
          <span className="home-src">{it.sourceName}</span>
          <span className="muted" title={fmtDateTime(it.publishedAt)}>
            {ago(it.publishedAt)}
          </span>
        </div>
        <button type="button" className="home-feed-title" onClick={() => onOpen(it)} title={it.summary}>
          {it.title}
        </button>
      </div>
      {canCreate && (
        <Link className="home-feed-new" to={`/story/new?feedItem=${it.id}`} title={t('feedsNewStory')}>
          ✚
        </Link>
      )}
    </li>
  );
}


/** The user's own choice of feeds for the start screen: every source grouped as the inbox groups them; none selected means all. */
function FeedChooser({ sources, chosen, onClose, onSave }: { sources: FeedSourceDto[]; chosen: string[] | null; onClose: () => void; onSave: (ids: string[] | null) => Promise<void> }) {
  const [sel, setSel] = useState<ReadonlySet<string>>(() => new Set(chosen && chosen.length ? chosen.filter((id) => sources.some((s) => s.id === id)) : sources.map((s) => s.id)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const groups = useMemo(() => {
    const m = new Map<string, FeedSourceDto[]>();
    for (const s of sources) {
      const k = s.groupName || t('feedsOther');
      m.set(k, [...(m.get(k) ?? []), s]);
    }
    return [...m.entries()];
  }, [sources]);
  const toggle = (ids: string[], on: boolean) =>
    setSel((prev) => {
      const n = new Set(prev);
      for (const id of ids) {
        if (on) n.add(id);
        else n.delete(id);
      }
      return n;
    });
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const all = sel.size === 0 || sel.size === sources.length;
      await onSave(all ? null : sources.filter((s) => sel.has(s.id)).map((s) => s.id));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog title={t('homeChooseFeeds')} onClose={onClose} width={720}>
      <p className="muted">{t('homeChooseFeedsHint')}</p>
      <div className="toolbar left">
        <button type="button" className="btn small" onClick={() => toggle(sources.map((s) => s.id), true)}>
          {t('selectAll')}
        </button>
        <button type="button" className="btn small" onClick={() => toggle(sources.map((s) => s.id), false)}>
          {t('selectNone')}
        </button>
      </div>
      <div className="feed-chooser">
        {groups.map(([g, list]) => {
          const ids = list.map((s) => s.id);
          const onCount = ids.filter((id) => sel.has(id)).length;
          return (
            <div key={g} className="feed-chooser-group">
              <label>
                <input
                  type="checkbox"
                  checked={onCount === ids.length}
                  ref={(el) => {
                    if (el) el.indeterminate = onCount > 0 && onCount < ids.length;
                  }}
                  onChange={(e) => toggle(ids, e.target.checked)}
                />
                {g}
              </label>
              {list.map((s) => (
                <label key={s.id}>
                  <input type="checkbox" checked={sel.has(s.id)} onChange={(e) => toggle([s.id], e.target.checked)} />
                  {s.name}
                  <span className="muted">{s.itemCount}</span>
                </label>
              ))}
            </div>
          );
        })}
      </div>
      {error && <div className="banner err">{error}</div>}
      <div className="toolbar">
        <button type="button" className="btn primary" disabled={busy} onClick={() => void save()}>
          {t('save')}
        </button>
        <button type="button" className="btn" onClick={onClose}>
          {t('cancel')}
        </button>
      </div>
    </Dialog>
  );
}

export function HomePage() {
  const { session, can, updatePreferences } = useSession();
  const now = useClock();
  const locale = session?.user.locale === 'en' ? 'en-GB' : 'el-GR';
  const longDate = new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(now);

  const canRead = can('story.read');
  // the user's own choice of feeds (null = all), and a reload the moment the server announces new items
  const chosen = session?.user.preferences.homeFeedSources ?? null;
  const chosenKey = chosen && chosen.length ? [...chosen].sort().join(',') : '';
  const [feedBump, setFeedBump] = useState(0);
  useEffect(
    () =>
      realtime.listen((m: WsMessage) => {
        if (m.type === 'feeds') setFeedBump((n) => n + 1);
      }),
    [],
  );
  const [chooser, setChooser] = useState(false);
  // the item open in the reading pane
  const [reader, setReader] = useState<FeedItemDto | null>(null);
  // pictures whose address did not load: they leave the featured strip and the rows show no thumbnail
  const [badImages, setBadImages] = useState<ReadonlySet<string>>(() => new Set());
  const markBad = useCallback((id: string) => setBadImages((s) => (s.has(id) ? s : new Set([...s, id]))), []);
  const today = usePolled<{ date: string; rundowns: TodayRundown[] }>('/api/rundowns/today');
  const stories = usePolled<{ total: number; stories: DayStory[] }>(canRead ? '/api/stories/day?size=10' : null);
  const sources = usePolled<{ sources: FeedSourceDto[] }>(canRead ? '/api/feeds/sources' : null, 5 * 60_000);
  const feed = usePolled<{ items: FeedItemDto[] }>(canRead ? `/api/feeds/items?limit=120${chosenKey ? `&sources=${chosenKey}` : ''}` : null, 60_000, feedBump);
  const allSources = sources.data?.sources ?? NO_SOURCES;

  const rundowns = today.data?.rundowns ?? [];
  const rowsToday = rundowns.reduce((a, r) => a + r.count, 0);
  const items = feed.data?.items ?? NO_ITEMS;
  const hourAgo = now.getTime() - 3_600_000;
  const lastHour = items.filter((it) => new Date(it.publishedAt).getTime() > hourAgo).length;

  // the local wires in their own column, the rest of the world beside them; the same headline
  // carried by two wires (BBC World and BBC Europe, say) is shown once, from the newest wire
  const { featured, local, world, localName, worldName, twoColumns } = useMemo(() => {
    const chosenSet = chosenKey ? new Set(chosenKey.split(',')) : null;
    const groupsInOrder: string[] = [];
    for (const s of allSources) if (s.groupName && (!chosenSet || chosenSet.has(s.id)) && !groupsInOrder.includes(s.groupName)) groupsInOrder.push(s.groupName);
    for (const it of items) if (it.groupName && !groupsInOrder.includes(it.groupName)) groupsInOrder.push(it.groupName);
    const localGroup = groupsInOrder.find((g) => LOCAL_GROUP.test(g)) ?? groupsInOrder[0] ?? '';
    const isLocal = (it: FeedItemDto) => (it.groupName || '') === localGroup;
    const seen = new Set<string>();
    const unique = items.filter((it) => {
      const key = it.title.trim().toLocaleLowerCase();
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    const featuredIds = new Set<string>();
    const featured: FeedItemDto[] = [];
    for (const it of unique) {
      if (featured.length >= FEATURED_MAX) break;
      if (it.imageUrl && !badImages.has(it.id)) {
        featured.push(it);
        featuredIds.add(it.id);
      }
    }
    const rest = unique.filter((it) => !featuredIds.has(it.id));
    const others = groupsInOrder.filter((g) => g !== localGroup);
    const world = rest.filter((it) => !isLocal(it)).slice(0, PER_COLUMN);
    return {
      featured,
      local: rest.filter(isLocal).slice(0, PER_COLUMN),
      world,
      localName: localGroup || t('homeFeedsLocal'),
      worldName: others.length ? others.join(' · ') : t('homeFeedsWorld'),
      twoColumns: others.length > 0 || world.length > 0,
    };
  }, [items, allSources, chosenKey, badImages]);
  const chosenCount = chosenKey ? chosenKey.split(',').length : 0;

  // the strip: always two full rows, as many across as the width takes (fewer when the pictures run short)
  const [stripEl, setStripEl] = useState<HTMLDivElement | null>(null);
  const layout = useStripLayout(stripEl, featured.length > 0);
  const strip = useMemo(() => {
    const across = Math.max(2, Math.min(layout.cols || 6, Math.floor(featured.length / 2)));
    return { cols: across, items: featured.slice(0, across * 2) };
  }, [featured, layout.cols]);
  // the type and the padding grow with the card
  const k = layout.scale;

  const canCreate = can('story.create');
  const dayStories = stories.data?.stories ?? [];
  const statusClass = (s: string) => (s === 'published' ? 'ok' : s === 'linked' ? 'info' : '');
  const storyHref = (s: DayStory) => (s.rundownId ? `/rundown/${s.rundownId}` : `/story/${s.id}`);
  const emptyFeed = items.length === 0 ? t('homeNoFeeds') : t('feedsEmpty');

  return (
    <div className="home">
      <section className="home-hero">
        <div className="home-hero-text">
          <div className="home-greeting">
            {greeting(now.getHours())}, {session?.user.displayName}
          </div>
          <h1 className="home-welcome">{t('welcome')}</h1>
          <div className="home-actions">
            {can('story.create') && (
              <Link className="btn primary" to="/story/new">
                ✚ {t('newStory')}
              </Link>
            )}
            <Link className="btn" to="/rundown">
              {t('rundownGuide')}
            </Link>
            <Link className="btn" to="/stories/day">
              {t('dayStories')}
            </Link>
            <Link className="btn" to="/feeds">
              {t('tabFeeds')}
            </Link>
          </div>
        </div>
        <div className="home-clock" aria-live="off">
          <div className="home-time">
            {pad2(now.getHours())}:{pad2(now.getMinutes())}
            <span className="home-seconds">:{pad2(now.getSeconds())}</span>
          </div>
          <div className="home-date">{longDate}</div>
          <div className="home-kpis">
            <span>
              <b>{rundowns.length}</b> {t('homeKpiBulletins')}
            </span>
            <span>
              <b>{rowsToday}</b> {t('homeKpiRows')}
            </span>
            {canRead && (
              <span>
                <b>{lastHour}</b> {t('homeKpiFeedsHour')}
              </span>
            )}
          </div>
        </div>
      </section>

      {(today.error || stories.error || feed.error) && <div className="banner err">{today.error ?? stories.error ?? feed.error}</div>}

      {canRead && (
        <div ref={setStripEl} className="home-featured" style={{ gridTemplateColumns: `repeat(${strip.cols}, minmax(0, 1fr))` }}>
          {strip.items.map((it) => (
            <button type="button" key={it.id} className="home-featured-card" onClick={() => setReader(it)} title={it.summary}>
              <img src={it.imageUrl!} alt="" loading="lazy" referrerPolicy="no-referrer" onError={() => markBad(it.id)} />
              <div className="home-featured-caption" style={{ padding: `${Math.round(22 * k)}px ${Math.round(8 * k)}px ${Math.round(7 * k)}px`, gap: Math.round(3 * k) }}>
                <span className="home-src light" style={{ fontSize: Math.min(13, Math.round(10 * k * 10) / 10) }}>
                  {it.sourceName} · {ago(it.publishedAt)}
                </span>
                <span className="home-featured-title" style={{ fontSize: Math.round(11.5 * k * 10) / 10 }}>
                  {it.title}
                </span>
              </div>
            </button>
          ))}
        </div>
      )}
      <div className="home-grid">
        <section className="home-card home-today">
          <header className="home-card-head">
            <h2>{t('todaysRundowns')}</h2>
            <Link to="/rundown">{t('rundownGuide')} →</Link>
          </header>
          {rundowns.length === 0 ? (
            <p className="home-empty">{t('homeNoRundowns')}</p>
          ) : (
            <ul className="home-list">
              {rundowns.map((r) => (
                <li key={r.id} className="home-rundown">
                  <Link to={`/rundown/${r.id}`} className="home-rundown-name">
                    {r.name}
                  </Link>
                  <span className="muted">
                    {r.count} {t('homeRowsUnit')}
                  </span>
                  <span className={`status ${statusClass(r.status)}`}>{t(r.status as 'draft' | 'linked' | 'published')}</span>
                </li>
              ))}
            </ul>
          )}
          {canRead && (
            <>
              <header className="home-card-head sub">
                <h2>{t('homeLatestStories')}</h2>
                <Link to="/stories/day">{t('dayStories')} →</Link>
              </header>
              {dayStories.length === 0 ? (
                <p className="home-empty">{t('homeNoStories')}</p>
              ) : (
                <ul className="home-list">
                  {dayStories.slice(0, 8).map((s) => (
                    <li key={s.id} className="home-story">
                      <Link to={storyHref(s)} className="home-story-title" title={s.title}>
                        {s.title || '—'}
                      </Link>
                      <div className="home-story-meta muted">
                        {s.programmeName}
                        {s.createdBy ? ` · ${s.createdBy}` : ''} · {ago(s.updatedAt)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </section>

        {canRead && (
          <section className="home-feeds">
            <div className={twoColumns ? 'home-feed-columns' : 'home-feed-columns single'}>
              <section className="home-card">
                <header className="home-card-head">
                  <h2>{localName}</h2>
                  <div className="home-head-tools">
                    {feed.at && (
                      <span className="muted home-updated" title={t('homeUpdated')}>
                        ↻ {pad2(feed.at.getHours())}:{pad2(feed.at.getMinutes())}:{pad2(feed.at.getSeconds())}
                      </span>
                    )}
                    <button type="button" className="btn small" onClick={() => setChooser(true)} title={t('homeChooseFeedsHint')}>
                      ⚙ {t('homeChooseFeeds')}
                      {chosenCount ? ` (${chosenCount}/${allSources.length})` : ''}
                    </button>
                    <Link to="/feeds">{t('homeAllFeeds')} →</Link>
                  </div>
                </header>
                {local.length === 0 ? (
                  <p className="home-empty">{emptyFeed}</p>
                ) : (
                  <ul className="home-list">
                    {local.map((it) => (
                      <FeedRow key={it.id} it={it} canCreate={canCreate} onOpen={setReader} />
                    ))}
                  </ul>
                )}
              </section>
              {twoColumns && (
                <section className="home-card">
                  <header className="home-card-head">
                    <h2>{worldName}</h2>
                    <Link to="/feeds">{t('homeAllFeeds')} →</Link>
                  </header>
                  {world.length === 0 ? (
                    <p className="home-empty">{emptyFeed}</p>
                  ) : (
                    <ul className="home-list">
                      {world.map((it) => (
                        <FeedRow key={it.id} it={it} canCreate={canCreate} onOpen={setReader} />
                      ))}
                    </ul>
                  )}
                </section>
              )}
            </div>
          </section>
        )}
      </div>
      {reader && <FeedItemDialog item={reader} canCreate={canCreate} onClose={() => setReader(null)} />}
      {chooser && (
        <FeedChooser
          sources={allSources}
          chosen={chosen}
          onClose={() => setChooser(false)}
          onSave={async (ids) => {
            await updatePreferences({ homeFeedSources: ids });
            setChooser(false);
          }}
        />
      )}
    </div>
  );
}
